/**
 * One coverage line: "n von m verifiziert", the open remainder, and a bar.
 *
 * The bar is decoration on top of the numbers, never the carrier of the fact —
 * the counts stand in text right above it, and the open count stands below it
 * in the same weight as the verified one. A reader who cannot see the bar at
 * all loses nothing, which is the WCAG requirement and also the honest layout:
 * "23 offen" should not be smaller than "5 verifiziert".
 */
import { useI18n } from '@/i18n'

import { openCount, verifiedPercent } from './rulebase'
import type { StatusCount } from '@/api/types'

export function CoverageMeter({
  label,
  counts,
  testId,
}: {
  label: string
  counts: StatusCount
  testId?: string
}) {
  const { t } = useI18n()
  const total = counts.total ?? 0
  const open = openCount(counts)
  const percent = verifiedPercent(counts)

  return (
    <div className="flex flex-col gap-1.5" data-testid={testId} data-open={open}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[13px] font-medium text-ink-2">{label}</span>
        <span className="num text-[13px] text-ink-2">
          {counts.verified} {t('common.of')} {total} {t('rulebase.verified')}
        </span>
      </div>
      <div
        role="meter"
        aria-label={label}
        aria-valuenow={counts.verified}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuetext={t('verify.progress', { verified: counts.verified, total })}
        className="h-1.5 w-full overflow-hidden rounded-pill bg-surface-2"
      >
        <div className="h-full rounded-pill bg-accent" style={{ width: `${percent}%` }} />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-muted">
        <span>
          <span className="num">{open}</span> {t('rulebase.open')}
        </span>
        {counts.placeholder > 0 ? (
          <span>
            <span className="num">{counts.placeholder}</span> {t('coverage.placeholder')}
          </span>
        ) : null}
      </div>
    </div>
  )
}
